/**
 * Micheals Infrastructure Advisory - PRINCIPAL SALES ENGINE & ORCHESTRATOR
 * ROLE: Routes consultation lifecycles and manages AVA Intelligence.
 * VERSION: 10.4.3 (Build 2026.05.07 - Forensic Portal Sync)
 * FIRM: Micheals Infrastructure Advisory 
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async'; 
import { AnimatePresence } from 'framer-motion';

// --- SYSTEM CORE ---
import { useSalesEngine } from './hooks/useSalesEngine';
import { useAvaPolling } from './hooks/useAvaPolling';
import { businessConfig } from './config/businessConfig';

// --- INFRASTRUCTURE UI ---
import SalesModal from './components/StrategicAnalysisModal';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// --- PRINCIPAL INTERFACES ---
import LandingPage from './components/LandingPage';
import TechnicalDesign from './pages/TechnicalDesign'; 
import DesignSpecs from './pages/DesignSpecs'; 
import AboutPage from './pages/AboutPage'; 
import Services from './pages/Services'; 
import FAQ from './pages/FAQ'; 
import Success from './pages/Success'; 
import BookingPage from './pages/BookingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import BlogSmartLighting from './pages/BlogSmartLighting';

// --- WORKFLOW NODES (CLIENT PORTAL & ONBOARDING) ---
import IntakeSuccess from './components/IntakeSuccess'; 
import DiscoveryPortal from './components/Onboarding/DiscoveryPortal';
import SchedulingGatekeeper from './pages/SchedulingGatekeeper'; 
import ProjectArchive from './pages/Client/ProjectArchive'; 

// --- COMMAND & CONTROL ---
import AdminLogin from './pages/Admin/AdminLogin';
import PrincipalCommandCenter from './pages/Admin/PrincipalCommandCenter'; 

function App() {
  const location = useLocation();
  const [isSnoozed, setIsSnoozed] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('ava_admin_session'));

  /**
   * 1. ENVIRONMENT RESOLUTION
   * Subdomain Partitioning: API (HTTP) vs AVA (WSS)
   * Ensures WebSocket upgrades are routed to the logic-first backend.
   */
  const API_CONFIG = useMemo(() => {
    const isLocal = window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1');
    return {
      HTTP: isLocal ? 'http://localhost:5001' : 'https://api.medconsulting.solutions',
      WSS: isLocal ? 'ws://localhost:5001' : 'wss://ava.medconsulting.solutions'
    };
  }, []);

  /**
   * 2. FORENSIC IDENTITY ANCHOR
   * Prevents session fragmentation across the architectural nodes.
   */
  const sessionId = useMemo(() => {
    const forensicId = window.AVA_SESSION_ID || localStorage.getItem('session_id');
    
    if (!forensicId || forensicId === "null" || forensicId === "undefined") {
      const fallbackId = `node_v10_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('session_id', fallbackId);
      window.AVA_SESSION_ID = fallbackId;
      return fallbackId;
    }
    return forensicId;
  }, []);

  /**
   * 3. INTELLIGENCE HANDSHAKE (AVA SOCKET)
   */
  const { socket, isConnected } = useAvaPolling(API_CONFIG.WSS, sessionId);
  
  const { 
    syncLeadData: syncWithAva, 
    isModalOpen, 
    nudgeMessage, 
    setIsModalOpen, 
    isTyping, 
    handleUserChoice,
    debugScore,
    studiedContext 
  } = useSalesEngine(sessionId, API_CONFIG.HTTP);

  /**
   * 4. SYSTEM IDENTIFICATION
   */
  useEffect(() => {
    if (isConnected && socket) {
      const shortId = String(sessionId).split('_')[2] || 'UNKNOWN';
      console.log(`%c 🛰️ [AVA_OS]: NODE_${shortId} SYNCHRONIZED `, "background: #1c1917; color: #fbbf24; font-weight: bold; padding: 2px 5px;");
      
      socket.emit('NODE_IDENTIFY', {
        agent: navigator.userAgent,
        build: "10.4.3",
        sessionId: sessionId,
        origin: window.location.origin
      });
    }
  }, [isConnected, socket, sessionId]);

  /**
   * 5. TELEMETRY & CONVERSION TRACKING
   */
  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    
    if (!isAdmin && typeof syncWithAva === 'function') {
      const pathwayLabel = `NAV_${location.pathname.toUpperCase().replace(/\//g, '_') || 'ROOT'}`;
      
      syncWithAva({ 
        type: pathwayLabel,
        metadata: {
            isConversionNode: ['/success', '/intake-success', '/archive'].some(path => location.pathname.includes(path)),
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString()
        }
      });

      // Snooze SalesModal on high-intent or success pages
      if (location.pathname === '/success' || location.pathname.startsWith('/archive') || location.pathname === '/discovery') {
        setIsSnoozed(true);
      }
    }
  }, [location.pathname, syncWithAva]);

  const handleModalClose = useCallback(() => {
    localStorage.setItem('ava_last_dismissed', Date.now().toString());
    setIsSnoozed(true);
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <HelmetProvider>
      <div className="App selection:bg-amber-100 selection:text-stone-900 bg-[#f8f8f7] min-h-screen font-sans antialiased overflow-x-hidden">
        <Helmet>
          <title>{businessConfig.name} | Principal Architectural Oversight</title>
          <meta httpEquiv="Content-Security-Policy" content={`
            default-src 'self';
            script-src 'self' 'unsafe-inline' 'unsafe-eval' blob: https://*.stripe.com https://p.trellocdn.com;
            style-src 'self' 'unsafe-inline' https://fonts.googleapis.com;
            font-src 'self' https://fonts.gstatic.com data:;
            img-src 'self' data: blob: https://*.stripe.com https://*.s3.amazonaws.com https://api.medconsulting.solutions;
            connect-src 'self' 
              http://localhost:5001 
              ws://localhost:5001 
              http://127.0.0.1:5001
              ws://127.0.0.1:5001
              https://api.medconsulting.solutions 
              wss://api.medconsulting.solutions 
              https://ava.medconsulting.solutions
              wss://ava.medconsulting.solutions
              https://api.stripe.com;
            frame-src 'self' https://*.stripe.com https://calendly.com;
          `} />
        </Helmet>

        <ScrollToTop /> 
        
        {!isAdminRoute && <Navbar isConnected={isConnected} />}
        
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* CORE PAGES */}
            <Route path="/" element={<LandingPage sessionId={sessionId} />} /> 
            <Route path="/about" element={<AboutPage sessionId={sessionId} />} />
            <Route path="/design-architecture" element={<TechnicalDesign sessionId={sessionId} />} />
            <Route path="/design-specs" element={<DesignSpecs sessionId={sessionId} />} />
            <Route path="/services" element={<Services sessionId={sessionId} />} />
            <Route path="/faq" element={<FAQ sessionId={sessionId} />} />
            
            {/* CLIENT ONBOARDING & FORENSIC WORKFLOW */}
            <Route path="/discovery" element={<DiscoveryPortal sessionId={sessionId} />} />
            <Route path="/book-consultation" element={<BookingPage sessionId={sessionId} />} />
            <Route path="/success" element={<Success />} /> 
            <Route path="/intake-success" element={<IntakeSuccess />} />
            <Route path="/archive/:sessionId" element={<ProjectArchive backendUrl={API_CONFIG.HTTP} />} />
            <Route path="/schedule/:briefId" element={<SchedulingGatekeeper backendUrl={API_CONFIG.HTTP} />} />
            
            {/* LEGAL & BLOG */}
            <Route path="/blog/smart-lighting" element={<BlogSmartLighting sessionID={sessionId} />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* ADMINISTRATIVE COMMAND & CONTROL */}
            <Route 
              path="/admin/*" 
              element={
                adminToken ? (
                  <PrincipalCommandCenter token={adminToken} backendUrl={API_CONFIG.HTTP} />
                ) : (
                  <AdminLogin onLogin={(token) => {
                    setAdminToken(token);
                    localStorage.setItem('ava_admin_session', token);
                  }} />
                )
              } 
            />

            {/* FALLBACK REDIRECT */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>

        {!isAdminRoute && (
          <>
            <SalesModal 
              isOpen={isModalOpen && !isSnoozed} 
              message={nudgeMessage} 
              isTyping={isTyping}
              handleUserChoice={handleUserChoice}
              onClose={handleModalClose}
              studiedContext={studiedContext} 
              sessionId={sessionId}
            />
            <Footer sessionId={sessionId} />
          </>
        )}

        {/* LOGIC-FIRST DEBUG HUD (LOCAL ONLY) */}
        {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
          <div className="fixed bottom-4 left-4 p-5 bg-stone-950/98 backdrop-blur-2xl rounded-sm text-[10px] font-mono z-[10000] border border-stone-800 shadow-2xl">
            <div className="flex items-center gap-3 mb-3 border-b border-stone-800 pb-3">
              <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-amber-500 animate-pulse' : 'bg-red-500'}`}></span>
              <span className="font-black tracking-[0.2em] uppercase text-stone-300">MED_OS // ANALYTICS_V10.4.3</span>
            </div>
            <div className="space-y-1 text-stone-500">
              <div className="flex justify-between gap-10"><span>INTENT:</span> <span className="text-white">{debugScore}%</span></div>
              <div className="flex justify-between gap-10"><span>NODE:</span> <span className="text-amber-500 uppercase">{location.pathname}</span></div>
              <div className="flex justify-between gap-10"><span>SESSION:</span> <span className="text-stone-300">{sessionId?.split('_')[2]}</span></div>
            </div>
          </div>
        )}
      </div>
    </HelmetProvider>
  );
}

export default App;